import React, { useMemo, useEffect, useState } from "react"
import { View, ScrollView } from "react-native"
import { TouchableRipple, Checkbox } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import {
    constraintsGridDays,
    localizeGridTime,
    shiftedDayLabel,
} from "./constraintsGrid"

const ConstraintsGridStep = (props) => {
    const {
        field,
        setFormValues,
        formValues,
        user,
        includeSunday = false,
        // Parent extra-hours form opts out of the long "Sélectionnez un
        // maximum de créneaux…" hint and the 100px tail padding. The
        // student onboarding flow keeps both (this is a multi-step
        // diagnostic where the encouragement matters).
        compact = false,
        // Optional Set of English day names (e.g. {"Monday","Tuesday"}).
        // When provided, days outside the set are hidden. Used by the
        // parent extra-hours form to keep the grid in sync with the
        // requested deadline: "sous 3 jours" on a Sunday would otherwise
        // let parents pick Friday, which the routing service can never
        // satisfy in-window.
        allowedDays = null,
        // Écart, en heures, entre l'horloge de la famille et celle du serveur.
        // Les cases NOMMENT une heure de Paris (c'est l'offre, et c'est ce
        // qu'on envoie) mais l'AFFICHENT à l'heure du lecteur : sans ça un
        // parent à l'étranger réserve une heure à côté de ce qu'il croit.
        displayOffsetHours = 0,
    } = props

    // Track which days are checked (all slots selected)
    const [checkedDays, setCheckedDays] = useState(new Set())
    // Track which days are expanded (showing time slots)
    const [expandedDays, setExpandedDays] = useState(new Set())

    const daySlots = useMemo(() => {
        const slots = constraintsGridDays({ includeSunday })

        if (allowedDays) {
            return slots.filter((s) => allowedDays.has(s.day))
        }

        return slots
    }, [includeSunday, allowedDays])

    // Function to sort slots by day order and then by time
    const sortSlots = (slots) => {
        const dayOrder = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
        ]

        return slots.sort((a, b) => {
            const [dayA, timeA] = a.split(" ")
            const [dayB, timeB] = b.split(" ")

            // First, sort by day
            const dayIndexA = dayOrder.indexOf(dayA)
            const dayIndexB = dayOrder.indexOf(dayB)

            if (dayIndexA !== dayIndexB) {
                return dayIndexA - dayIndexB
            }

            // Then sort by time (assuming format like "17h-18h")
            const timeNumberA = parseInt(timeA.split("h")[0])
            const timeNumberB = parseInt(timeB.split("h")[0])

            return timeNumberA - timeNumberB
        })
    }

    // Update checkedDays and expandedDays when field.value changes (handles pre-selected slots)
    useEffect(() => {
        const currentValue = field.value ?? []
        const newCheckedDays = new Set()
        const newExpandedDays = new Set()

        daySlots.forEach((daySlot) => {
            const allSlotsForDay = daySlot.times.map(
                (time) => `${daySlot.day} ${time}`
            )
            const atLeastOneSlotSelected =
                allSlotsForDay.length > 0 &&
                allSlotsForDay.some((slot) => currentValue.includes(slot))
            if (atLeastOneSlotSelected) {
                newCheckedDays.add(daySlot.day)
                newExpandedDays.add(daySlot.day)
            }
        })

        setCheckedDays(newCheckedDays)
        setExpandedDays(newExpandedDays)
    }, [field.value, daySlots])

    // Toggle an entire day (expand/collapse)
    const toggleDay = (day) => {
        const value = field.value ? [...field.value] : []
        const daySlot = daySlots.find((d) => d.day === day)

        if (!daySlot) return

        const allSlotsForDay = daySlot.times.map((time) => `${day} ${time}`)
        const isExpanded = expandedDays.has(day)

        if (isExpanded) {
            // Collapse day and remove all its slots
            const newValue = value.filter(
                (slot) => !allSlotsForDay.includes(slot)
            )
            const sortedValue = sortSlots([...newValue])

            setExpandedDays((prev) => {
                const newSet = new Set(prev)
                newSet.delete(day)
                return newSet
            })
            setCheckedDays((prev) => {
                const newSet = new Set(prev)
                newSet.delete(day)
                return newSet
            })

            field.onChange(sortedValue)
            setFormValues?.({
                ...formValues,
                [field.name]: sortedValue,
            })
        } else {
            // Just expand the day without selecting any slots
            setExpandedDays((prev) => new Set([...prev, day]))
        }
    }

    const toggleSlot = (slot) => {
        const value = field.value ? [...field.value] : []
        const idx = value.indexOf(slot)

        let newValue
        if (idx >= 0) {
            // Remove slot
            newValue = value.filter((s) => s !== slot)
        } else {
            // Add slot
            newValue = [...value, slot]
        }

        const sortedValue = sortSlots([...newValue])
        field.onChange(sortedValue)
        setFormValues?.({
            ...formValues,
            [field.name]: sortedValue,
        })

        // Update checkedDays based on whether any slots are selected for this day
        const [day] = slot.split(" ")
        const daySlot = daySlots.find((d) => d.day === day)
        if (daySlot) {
            const allSlotsForDay = daySlot.times.map((time) => `${day} ${time}`)
            const hasSelectedSlots = allSlotsForDay.some((s) =>
                sortedValue.includes(s)
            )

            setCheckedDays((prev) => {
                const newSet = new Set(prev)
                if (hasSelectedSlots) {
                    newSet.add(day)
                } else {
                    newSet.delete(day)
                }
                return newSet
            })
        }
    }

    const isSelected = (slot) => (field.value || []).includes(slot)

    return (
        <ScrollView
            style={{ width: "100%" }}
            contentContainerStyle={{
                paddingHorizontal: 10,
                paddingBottom: compact ? 8 : 100,
            }}
        >
            {daySlots.map((d) => {
                const selectedSlotsForDay = d.times.filter((t) =>
                    isSelected(`${d.day} ${t}`)
                ).length

                return (
                    <TouchableRippleM
                        key={d.day}
                        style={{
                            marginBottom: 12,
                            backgroundColor: theme.colors.white,
                            borderWidth: 1,
                            borderColor: checkedDays.has(d.day)
                                ? theme.colors.success
                                : theme.colors.light,
                            borderRadius: 12,
                            padding: 10,
                            opacity: expandedDays.has(d.day) ? 1 : 0.6,
                        }}
                        onPress={() => toggleDay(d.day)}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 10,
                            }}
                        >
                            <Checkbox.Item
                                label={d.label}
                                status={
                                    expandedDays.has(d.day)
                                        ? "checked"
                                        : "unchecked"
                                }
                                onPress={() => toggleDay(d.day)}
                                color={theme.colors.success}
                                position="leading"
                                style={{ padding: 0 }}
                                labelStyle={{
                                    fontSize: 16,
                                    fontFamily: "Montserrat-Bold",
                                }}
                            />
                            <TextM
                                fontSize={10}
                                color={theme.colors.light}
                                wrap
                                style={{ maxWidth: "50%" }}
                            >
                                {expandedDays.has(d.day)
                                    ? `${selectedSlotsForDay}/${d.times.length} créneaux sélectionné${selectedSlotsForDay > 1 ? "s" : ""}`
                                    : `${d.times.length} créneaux possibles`}
                            </TextM>
                        </View>
                        {expandedDays.has(d.day) && (
                            <View
                                style={{
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                }}
                            >
                                {d.times.map((t) => {
                                    const slot = `${d.day} ${t}`
                                    const selected = isSelected(slot)
                                    const local = localizeGridTime(
                                        t,
                                        displayOffsetHours
                                    )
                                    const shiftedDay = shiftedDayLabel(
                                        d.day,
                                        local.dayShift
                                    )
                                    const bg = selected
                                        ? theme.colors.successLight
                                        : theme.colors.whiteAlmost
                                    const textColor = selected
                                        ? theme.colors.success
                                        : theme.colors.light
                                    return (
                                        <TouchableRipple
                                            key={slot}
                                            onPress={() => toggleSlot(slot)}
                                            style={{
                                                backgroundColor: bg,
                                                paddingVertical: 10,
                                                paddingHorizontal: 14,
                                                borderRadius: 10,
                                                marginRight: 8,
                                                marginBottom: 8,
                                                borderWidth: 1,
                                                borderColor: selected
                                                    ? theme.colors.success
                                                    : theme.colors.light,
                                                borderStyle: selected
                                                    ? "solid"
                                                    : "dashed",
                                                minWidth: 84,
                                            }}
                                        >
                                            <View
                                                style={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <FontAwesome5
                                                    name="check"
                                                    size={20}
                                                    color={
                                                        selected
                                                            ? textColor
                                                            : "transparent"
                                                    }
                                                    style={{
                                                        marginRight: 6,
                                                        width: 20,
                                                    }}
                                                />
                                                <TextM
                                                    color={textColor}
                                                    fontWeight="Bold"
                                                    fontSize={12}
                                                >
                                                    {shiftedDay
                                                        ? `${shiftedDay} ${local.label}`
                                                        : local.label}
                                                </TextM>
                                            </View>
                                        </TouchableRipple>
                                    )
                                })}
                            </View>
                        )}
                    </TouchableRippleM>
                )
            })}
            {/* Legend */}
            <View style={{ marginBottom: 14 }}>
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginBottom: 4,
                    }}
                >
                    <FontAwesome5
                        name="check"
                        size={16}
                        color={theme.colors.success}
                        style={{ marginRight: 6, width: 16, height: 16 }}
                    />
                    <TextM color={theme.colors.success} fontSize={12} wrap>
                        Sélectionné
                    </TextM>
                    <View style={{ width: 16 }} />
                    <View
                        style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            backgroundColor: theme.colors.lightMore,
                            borderWidth: 1,
                            borderColor: theme.colors.light,
                            marginRight: 6,
                        }}
                    />
                    <TextM color={theme.colors.grey} fontSize={12} wrap>
                        Non sélectionné
                    </TextM>
                </View>
                {!compact && (
                    <TextM
                        color={theme.colors.light}
                        fontSize={12}
                        wrap
                        style={{ marginTop: 10 }}
                    >
                        Sélectionnez un maximum de créneaux possibles pour
                        faciliter la recherche du masteur parfait pour{" "}
                        {user?.firstname}
                    </TextM>
                )}
            </View>
        </ScrollView>
    )
}

export default ConstraintsGridStep
